"use client";

import { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { fromTimestamp } from "@/lib/firestore";
import { relativeDate } from "@/lib/humanDate";
import type { HouseholdCalendar } from "@/lib/models";

interface CalendarSyncSettingsModalProps {
  onClose: () => void;
}

interface SyncResult {
  added?: number;
  results?: Array<{ label?: string; added?: number; error?: string }>;
  message?: string;
}

/**
 * Calendar sync settings sheet — manages the household's iCal feed
 * subscriptions. Mirrors iOS
 * `Views/Events/EventsView.swift:669-963 CalendarSyncSettingsView`.
 *
 * Capabilities:
 *   1. List existing feeds with health badges (last sync, consecutive
 *      failures, paused) and a per-feed delete affordance.
 *   2. Add a new feed (`url` required; `label` + `filter` optional).
 *      Stored at `households/{id}.calendars[]`.
 *   3. "Sync now" — invokes the existing `syncCalendarOnDemand`
 *      callable Cloud Function for the current household.
 *
 * The daily scheduled sync (`scheduledCalendarSync`) is owned by
 * Cloud Functions; this UI only triggers on-demand refreshes.
 */
export function CalendarSyncSettingsModal({ onClose }: CalendarSyncSettingsModalProps) {
  const { householdId, household } = useHouseholdContext();
  const t = useTranslations();

  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newFilter, setNewFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const calendars = household?.calendars ?? [];

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdId) return;
    const url = newUrl.trim();
    if (!url) return;

    // Web-side: accept webcal://, http://, https:// — backend
    // normalizes webcal → https.
    if (!/^(https?|webcal):\/\//.test(url)) {
      setError(t("events.sync.invalidUrl"));
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const newFeed: HouseholdCalendar = {
        url,
        ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
        ...(newFilter.trim() ? { filter: newFilter.trim() } : {}),
      };
      const next = [...calendars, newFeed];
      await updateDoc(doc(db, "households", householdId), {
        calendars: next,
      });
      setNewUrl("");
      setNewLabel("");
      setNewFilter("");
    } catch (err) {
      console.error("Failed to add iCal feed:", err);
      setError(t("events.sync.errorAdd"));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (feed: HouseholdCalendar) => {
    if (!householdId) return;
    const next = calendars.filter((c) => c.url !== feed.url);
    try {
      await updateDoc(doc(db, "households", householdId), {
        calendars: next,
      });
    } catch (err) {
      console.error("Failed to delete iCal feed:", err);
    }
  };

  const handleSyncNow = async () => {
    if (!householdId) return;
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const functions = getFunctions(getApp());
      const sync = httpsCallable<{ householdId: string }, SyncResult>(
        functions,
        "syncCalendarOnDemand",
      );
      const result = await sync({ householdId });
      const added = result.data?.added ?? 0;
      setSyncResult(t("events.sync.successCount", { count: added }));
    } catch (err) {
      console.error("syncCalendarOnDemand failed:", err);
      setError(t("events.sync.errorSync"));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.close")}
          </button>
          <h2 className="font-bold text-base">{t("events.sync.title")}</h2>
          <button
            type="button"
            onClick={handleSyncNow}
            disabled={syncing || calendars.length === 0}
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {syncing ? t("events.sync.syncing") : t("events.sync.syncNow")}
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Existing feeds */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t("events.sync.feeds")}
            </h3>
            {calendars.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                {t("events.sync.empty")}
              </p>
            ) : (
              <div className="space-y-2">
                {calendars.map((feed) => (
                  <CalendarFeedRow
                    key={feed.url}
                    feed={feed}
                    onDelete={() => handleDelete(feed)}
                    t={t}
                  />
                ))}
              </div>
            )}
            {syncResult && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                {syncResult}
              </p>
            )}
          </div>

          {/* Add feed */}
          <form onSubmit={handleAdd} className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t("events.sync.addTitle")}
            </h3>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                {t("events.sync.urlLabel")}
              </label>
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://example.com/feed.ics"
                required
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                {t("events.sync.labelLabel")}
              </label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t("events.sync.labelPlaceholder")}
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                {t("events.sync.filterLabel")}
              </label>
              <input
                type="text"
                value={newFilter}
                onChange={(e) => setNewFilter(e.target.value)}
                placeholder={t("events.sync.filterPlaceholder")}
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                {t("events.sync.filterHelp")}
              </p>
            </div>

            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            <button
              type="submit"
              disabled={adding || !newUrl.trim()}
              className="w-full py-2 rounded-lg text-sm font-semibold text-white bg-[color:var(--accent-color)] disabled:opacity-50 transition-opacity"
            >
              {adding ? t("events.sync.adding") : t("events.sync.add")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

interface CalendarFeedRowProps {
  feed: HouseholdCalendar;
  onDelete: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function CalendarFeedRow({ feed, onDelete, t }: CalendarFeedRowProps) {
  const lastSync = fromTimestamp(feed.last_sync_at);
  const failures = feed.consecutive_failures ?? 0;
  const status: "ok" | "warn" | "error" =
    feed.disabled || failures >= 3
      ? "error"
      : failures > 0
      ? "warn"
      : "ok";
  const statusColor =
    status === "ok"
      ? "bg-emerald-500"
      : status === "warn"
      ? "bg-amber-500"
      : "bg-red-500";

  return (
    <div className="rounded-lg border border-gray-100 dark:border-white/10 p-3">
      <div className="flex items-start gap-2">
        <span
          className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${statusColor}`}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">
            {feed.label || feed.url}
          </p>
          <p className="text-xs text-gray-500 truncate" title={feed.url}>
            {feed.url}
          </p>
          {feed.filter && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              {t("events.sync.filterChip", { filter: feed.filter })}
            </p>
          )}
          <p className="text-[11px] text-gray-400 mt-1">
            {lastSync
              ? t("events.sync.lastSyncedAt", { when: relativeDate(lastSync) })
              : t("events.sync.neverSynced")}
            {feed.last_sync_count != null
              ? ` · ${t("events.sync.lastCount", { count: feed.last_sync_count })}`
              : ""}
          </p>
          {feed.last_error && status !== "ok" && (
            <p className="text-[11px] text-red-500 mt-0.5">
              {feed.last_error}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 text-gray-400 hover:text-red-500 p-1"
          aria-label={t("events.sync.deleteAria")}
          title={t("events.sync.deleteAria")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
